import { useState, type ReactNode } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { ArrowRight, Eye, EyeOff } from 'lucide-react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox, Select } from '@/components/ui/misc';
import { ApiRequestError } from '@/lib/api';
import { AuthLayout, FormError } from './auth-layout';
import { useSession, useSignup, useSignupOptions } from './use-session';

/** Mirrors the API's password policy, so the form says what is wrong before submitting. */
const password = z
  .string()
  .min(10, 'At least 10 characters')
  .refine((v) => /[a-z]/.test(v), 'Add a lowercase letter')
  .refine((v) => /[A-Z]/.test(v), 'Add an uppercase letter')
  .refine((v) => /[0-9]/.test(v), 'Add a number');

const schema = z.object({
  companyName: z.string().trim().min(2, 'Enter your company name'),
  name: z.string().trim().min(2, 'Enter your full name'),
  email: z.string().trim().email('Enter a valid work email'),
  phone: z
    .string()
    .trim()
    .refine((v) => v === '' || /^\+?[0-9 ()-]{6,}$/.test(v), 'Enter a valid phone number'),
  password,
  industry: z.string().min(1, 'Choose your industry'),
  teamSize: z.string(),
  emirate: z.string(),
  acceptTerms: z.boolean().refine((v) => v, 'Please accept the terms to continue'),
});

type FormValues = z.infer<typeof schema>;

const FIELD_NAMES = new Set<keyof FormValues>([
  'companyName',
  'name',
  'email',
  'phone',
  'password',
  'industry',
  'teamSize',
  'emirate',
  'acceptTerms',
]);

export function SignupPage() {
  const { data: user, isLoading } = useSession();
  const { data: options } = useSignupOptions();
  const signup = useSignup();
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      companyName: '',
      name: '',
      email: '',
      phone: '+971 ',
      password: '',
      industry: 'general',
      teamSize: '',
      emirate: 'Dubai',
      acceptTerms: false,
    },
  });

  if (isLoading) return null;
  if (user) return <Navigate to="/dashboard" replace />;

  const errors = form.formState.errors;

  const onSubmit = form.handleSubmit(async (values) => {
    setError(null);
    const phone = values.phone.trim();
    try {
      await signup.mutateAsync({
        companyName: values.companyName,
        name: values.name,
        email: values.email,
        password: values.password,
        industry: values.industry,
        acceptTerms: true,
        ...(phone && phone !== '+971' ? { phone } : {}),
        ...(values.teamSize ? { teamSize: values.teamSize } : {}),
        ...(values.emirate ? { emirate: values.emirate } : {}),
      });
    } catch (caught) {
      if (caught instanceof ApiRequestError) {
        let placed = false;
        for (const [field, message] of Object.entries(caught.fieldErrors)) {
          if (FIELD_NAMES.has(field as keyof FormValues)) {
            form.setError(field as keyof FormValues, { message });
            placed = true;
          }
        }
        if (!placed) setError(caught.message);
        return;
      }
      setError('Could not create your workspace. Please try again.');
    }
  });

  return (
    <AuthLayout
      title="Start your free trial"
      description="14 days of every feature. No credit card needed."
      footer={
        <>
          Already have a workspace?{' '}
          <Link to="/login" className="font-semibold text-link hover:underline">
            Sign in
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <Field label="Company name" htmlFor="companyName" error={errors.companyName?.message}>
          <Input
            id="companyName"
            autoComplete="organization"
            autoFocus
            placeholder="Al Noor Trading LLC"
            className="h-11 px-3.5"
            aria-invalid={Boolean(errors.companyName)}
            {...form.register('companyName')}
          />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Your name" htmlFor="name" error={errors.name?.message}>
            <Input
              id="name"
              autoComplete="name"
              className="h-11 px-3.5"
              aria-invalid={Boolean(errors.name)}
              {...form.register('name')}
            />
          </Field>
          <Field label="Mobile" htmlFor="phone" error={errors.phone?.message}>
            <Input
              id="phone"
              type="tel"
              autoComplete="tel"
              className="h-11 px-3.5"
              aria-invalid={Boolean(errors.phone)}
              {...form.register('phone')}
            />
          </Field>
        </div>

        <Field label="Work email" htmlFor="email" error={errors.email?.message}>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            className="h-11 px-3.5"
            aria-invalid={Boolean(errors.email)}
            {...form.register('email')}
          />
        </Field>

        <Field
          label="Password"
          htmlFor="password"
          error={errors.password?.message}
          hint="10+ characters with upper and lower case letters and a number."
        >
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              className="h-11 px-3.5 pe-11"
              aria-invalid={Boolean(errors.password)}
              {...form.register('password')}
            />
            <button
              type="button"
              onClick={() => setShowPassword((shown) => !shown)}
              className="absolute end-1 top-1/2 -translate-y-1/2 rounded-md p-2 text-muted-foreground transition-colors hover:text-foreground"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </Field>

        <Field label="Industry" htmlFor="industry" error={errors.industry?.message}>
          <Select id="industry" className="h-11 px-3.5" {...form.register('industry')}>
            {(options?.industries ?? [{ key: 'general', label: 'General / Other' }]).map((industry) => (
              <option key={industry.key} value={industry.key}>
                {industry.label}
              </option>
            ))}
          </Select>
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Team size" htmlFor="teamSize">
            <Select id="teamSize" className="h-11 px-3.5" {...form.register('teamSize')}>
              <option value="">Select…</option>
              {(options?.teamSizes ?? []).map((size) => (
                <option key={size} value={size}>
                  {size === '1' ? 'Just me' : `${size} people`}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Emirate" htmlFor="emirate">
            <Select id="emirate" className="h-11 px-3.5" {...form.register('emirate')}>
              <option value="">Select…</option>
              {(options?.emirates ?? []).map((emirate) => (
                <option key={emirate} value={emirate}>
                  {emirate}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <div className="space-y-1.5">
          <label className="flex items-start gap-2.5 text-sm text-slate-600">
            <Controller
              control={form.control}
              name="acceptTerms"
              render={({ field }) => (
                <Checkbox
                  className="mt-0.5"
                  checked={field.value}
                  onCheckedChange={(checked) => field.onChange(checked === true)}
                  aria-invalid={Boolean(errors.acceptTerms)}
                />
              )}
            />
            <span>
              I agree to the{' '}
              <Link to="/terms" className="font-medium text-link hover:underline">
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link to="/privacy" className="font-medium text-link hover:underline">
                Privacy Policy
              </Link>
              .
            </span>
          </label>
          {errors.acceptTerms && <p className="text-xs text-destructive">{errors.acceptTerms.message}</p>}
        </div>

        <FormError message={error} />

        <Button type="submit" size="lg" className="w-full shadow-lg" disabled={signup.isPending}>
          {signup.isPending ? 'Creating your workspace…' : 'Create my workspace'}
          {!signup.isPending && <ArrowRight className="h-4 w-4" />}
        </Button>
      </form>
    </AuthLayout>
  );
}

function Field({
  label,
  htmlFor,
  error,
  hint,
  children,
}: {
  label: string;
  htmlFor: string;
  error?: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {error ? (
        <p className="text-xs text-destructive">{error}</p>
      ) : hint ? (
        <p className="text-xs text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}
